import { setForceShowErrors } from '@nsr/react-form/dist/components/redux/slices/formSlice';
import { createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import findIndex from 'lodash/findIndex';
import { matchPath } from 'react-router-dom';
import { api } from '../../../ApiContext';
import { CONSTANTS, applicantRoute, routes } from '../../../Helpers/Configuration';
import { setApplicationPageCompletedStatus } from '../slices/applicationSlice';
import { setSettingsValue } from '../slices/settingsSlice';
import { startLoading, stopLoading } from '../../../../redux/shared/slices/isLoadingSlice';

const isNotDataEntryPage = (pathName) => {
    const match = matchPath(pathName, {
        path: [
            routes.adminTemplatePreviewReviewPage,
            routes.adminTemplatePreviewSubmitPage,
            routes.adminTemplatePreviewSupplementalItemsPage,
            routes.applicationFormReviewPage,
            routes.applicationFormSubmitPage,
            routes.applicationFormSupplementalItemsPage,
            routes.applicationFormPayments,
            routes.externalApplicationSubmitPage,
            routes.externalApplicationSupplementalItemsPage,
            routes.externalApplicationwReviewPage,
            routes.applicationFormWithdrawnPage,
            routes.externalApplicationPayments,
            routes.importsTemplatePreviewReviewPage,
            routes.importsTemplatePreviewSubmitPage,
            routes.importsTemplatePreviewSupplementalItemsPage,
        ],
    });
    return match !== null;
};
const isApplicationDataEntryPage = (pathName) => {
    return matchPath(pathName, {
        path: [
            routes.adminTemplatePreviewPageById,
            routes.applicationFormPageById,
            routes.externalApplicationPageById,
            routes.importsTemplatePreviewPageById,
            routes.importsApplicationEditPageById
        ]
    });
};
const pageHasErrors = (page, template, errors) => {
    if (!template.pages || !errors || page === undefined || !template.pages[page]) {
        return false;
    }
    return errors[template.pages[page].id] && Object.keys(errors[template.pages[page].id]).length > 0;
};
export const createDisableNavigationSelector = () =>
    createSelector(
        state => !!state.settings.inquiryMode,
        state => !!state.settings.supplementalFormMode,
        state => state.template,
        state => state.form.errors,
        (_, location) => location,
        (isInquiryMode, isSupplementalMode, template, errors, location) => {
            const page = isSupplementalMode ? 0 : getPageIndex(template.pages, location);
            if ((isInquiryMode || isSupplementalMode) && pageHasErrors(page, template, errors)) {
                return true;
            }
            return false;
        }
    );

const setPageRoute = (history, pageId, applicationId, externalMode, previewMode, importAdminMode, isAps) => {
    let route;
    switch (pageId) {
        case CONSTANTS.pageNames.review:
            route = applicantRoute(routes.applicationFormReviewPage, applicationId);
            if (previewMode && !isAps) {
                route = applicantRoute(routes.importsTemplatePreviewReviewPage, applicationId);
            }
            if (previewMode && isAps) {
                route = applicantRoute(routes.importsTemplatePreviewReviewPage, applicationId);
            }
            if (externalMode) {
                route = applicantRoute(routes.externalApplicationwReviewPage, applicationId);
            }
            history.push(route);
            break;
        case CONSTANTS.pageNames.supplementalItems:
            route = applicantRoute(routes.applicationFormSupplementalItemsPage, applicationId);
            if (previewMode && !isAps) {
                route = applicantRoute(routes.importsTemplatePreviewSupplementalItemsPage, applicationId);
            }
            if (previewMode && isAps) {
                route = applicantRoute(routes.importsTemplatePreviewSupplementalItemsPage, applicationId);
            }
            if (externalMode) {
                route = applicantRoute(routes.externalApplicationSupplementalItemsPage, applicationId);
            }
            history.push(route);
            break;
        case CONSTANTS.pageNames.submission:
            route = applicantRoute(routes.applicationFormSubmitPage, applicationId);
            if (previewMode && !isAps) {
                route = applicantRoute(routes.importsTemplatePreviewSubmitPage, applicationId);
            }
            if (previewMode && isAps) {
                route = applicantRoute(routes.importsTemplatePreviewSubmitPage, applicationId);
            }
            if (externalMode) {
                route = applicantRoute(routes.externalApplicationSubmitPage, applicationId);
            }
            history.push(route);
            break;
        default:
            route = applicantRoute(routes.applicationFormPageById, applicationId, pageId);
            if (previewMode && !isAps) {
                route = applicantRoute(routes.importsTemplatePreviewPageById, applicationId, pageId);
            }
            if (previewMode && isAps) {
                route = applicantRoute(routes.importsTemplatePreviewPageById, applicationId, pageId);
            }
            if (externalMode) {
                route = applicantRoute(routes.externalApplicationPageById, applicationId, pageId);
            }
            if (importAdminMode) {
                route = applicantRoute(routes.importsApplicationEditPageById, applicationId, pageId);
            }
            history.push(route);
    }
};

const setPageCompleteStatus = async (pageId, complete, application, values, revision, dispatch, skipApiUpdate = false) => {
    const completeStatus = complete ? CONSTANTS.applicationPageStatuses.COMPLETED : CONSTANTS.applicationPageStatuses.NOTCOMPLETED;
    const pageStatuses = {
        ...application.pageStatuses,
        [pageId]: completeStatus,
    };
    const newApplication = Object.assign({}, application, {
        values,
        pageStatuses,
        revision,
    });
    dispatch(setApplicationPageCompletedStatus({
        pageId,
        complete: completeStatus,
    }));
    let result = newApplication;
    if (!skipApiUpdate) {
        dispatch(startLoading());
        try {
            result = await api.applicationForms.update(newApplication);
        } finally {
            dispatch(stopLoading());
        }
    }
    dispatch(setSettingsValue({
        name: 'revision',
        value: result.revision,
    }));
};
export const setPageAndCloseDialogAction = createAsyncThunk(
    'navigation/setPageAndCloseDialog',
    async (arg, thunkApi) => {
        const {
            history,
            pageId,
            isAps
        } = arg;
        const { dispatch } = thunkApi;
        const state = thunkApi.getState();
        const isPreviewMode = state.settings.previewMode;
        const isInquiry = state.settings.inquiryMode;
        const isImportAdminMode = state.settings.importAdminMode;
        let page;
        const routeMatch = isApplicationDataEntryPage(history.location.pathname);
        if (routeMatch) {
            const pageIndex = findIndex(state.template.pages, { id: routeMatch.params.pageId });
            page = pageIndex;
        }
        if (!isNotDataEntryPage(history.location.pathname)) {
            if (pageHasErrors(page, state.template, state.form.errors)) {
                setPageCompleteStatus(
                    state.template.pages[page].id, false, state.application, state.form.values, state.settings.revision, dispatch, (isPreviewMode || isInquiry || isImportAdminMode)
                );

                dispatch(setForceShowErrors({
                    name: state.template.pages[page].id,
                    value: true,
                }));
            }
            else if (page >= 0) {
                setPageCompleteStatus(
                    state.template.pages[page].id, true, state.application, state.form.values, state.settings.revision, dispatch, (isPreviewMode || isInquiry || isImportAdminMode)
                );

                dispatch(setForceShowErrors({
                    name: state.template.pages[page].id,
                    value: false,
                }));
            }
        }
        setPageRoute(history, pageId, state.application.id, state.settings.externalMode, state.settings.previewMode, state.settings.importAdminMode, isAps);
        dispatch(setSettingsValue({
            name: 'showMobileSidebarDialog',
            value: false,
        }));
        return Promise.resolve(true);
    }
);

export const setPagesInitialStatusAction = createAsyncThunk(
    'navigation/setPagesInitialStatusAction',
    async (arg, thunkApi) => {

        const { dispatch } = thunkApi;
        const state = thunkApi.getState();

        Object.entries(state.application.pageStatuses).filter(([, status]) => status == CONSTANTS.applicationPageStatuses.NOTCOMPLETED).map(([pageId]) => pageId).forEach(pageId => {
            dispatch(setForceShowErrors({
                name: pageId,
                value: true,
            }));
        });

        return Promise.resolve(true);
    }
);
const getPageIndex = (pages, pathname) => {
    let page;
    const routeMatch = isApplicationDataEntryPage(pathname);
    if (routeMatch) {
        const pageIndex = findIndex(pages, { id: routeMatch.params.pageId });
        page = pageIndex;
    }
    return page;
};

export const goBackAction = createAsyncThunk(
    'navigation/goBack',
    async (arg, thunkApi) => {
        const {
            history,
            previousPageId,
            isAps
        } = arg;
        const { dispatch } = thunkApi;
        const state = thunkApi.getState();
        const page = getPageIndex(state.template.pages, history.location.pathname);
        if (state.settings.inquiryMode && pageHasErrors(page, state.template, state.form.errors)) {
            return;
        }
        dispatch(setPageAndCloseDialogAction({
            history,
            pageId: previousPageId,
            isAps
        }));
    }
);

export const goForwardAction = createAsyncThunk(
    'navigation/goForward',
    async (arg, thunkApi) => {
        const {
            history,
            nextPageId,
            isAps
        } = arg;
        const { dispatch } = thunkApi;
        const state = thunkApi.getState();
        const pageIndex = getPageIndex(state.template.pages, history.location.pathname);
        if (state.settings.inquiryMode && pageHasErrors(pageIndex, state.template, state.form.errors)) {
            return;
        }
        if (!nextPageId) {
            dispatch(setPageAndCloseDialogAction({
                history,
                pageId: CONSTANTS.pageNames.review,
                isAps
            }));
            return;
        }
        dispatch(setPageAndCloseDialogAction({
            history,
            pageId: nextPageId,
            isAps
        }));
    }
);
